/**
 * useCABWebSocket — React hook for CAB WebSocket connection.
 *
 * Manages connection lifecycle, provides progress state, and
 * exposes event handlers for the CABPage component.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { cabWebSocketManager, type CABWebSocket } from '../services/cabWebsocketService';
import type {
  CABWebSocketMessage,
  CABSessionStatus,
  CABProgressResponse,
} from '../types/cab';

interface UseCABWebSocketOptions {
  projectId: string | undefined;
  enabled?: boolean;
  onProgressUpdate?: (data: any) => void;
  onPhaseCompleted?: (phase: string) => void;
  onSectionReady?: (section: string) => void;
  onSkeletonReady?: () => void;
  onCompleted?: (data: any) => void;
  onError?: (data: any) => void;
  onPaused?: (data: any) => void;
  onCancelled?: (data: any) => void;
}

interface UseCABWebSocketReturn {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
}

export function useCABWebSocket({
  projectId,
  enabled = true,
  onProgressUpdate,
  onPhaseCompleted,
  onSectionReady,
  onSkeletonReady,
  onCompleted,
  onError,
  onPaused,
  onCancelled,
}: UseCABWebSocketOptions): UseCABWebSocketReturn {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<CABWebSocket | null>(null);

  // Store latest callbacks in refs to avoid reconnect cycles
  const callbacksRef = useRef({
    onProgressUpdate,
    onPhaseCompleted,
    onSectionReady,
    onSkeletonReady,
    onCompleted,
    onError,
    onPaused,
    onCancelled,
  });
  callbacksRef.current = {
    onProgressUpdate,
    onPhaseCompleted,
    onSectionReady,
    onSkeletonReady,
    onCompleted,
    onError,
    onPaused,
    onCancelled,
  };

  const handleMessage = useCallback((message: CABWebSocketMessage) => {
    const { type, data } = message;
    const cbs = callbacksRef.current;

    console.log(`📡 CAB Hook: Event [${type}]`, data);

    switch (type) {
      case 'cab_progress_update':
        cbs.onProgressUpdate?.(data);
        break;
      case 'cab_phase_completed':
        cbs.onPhaseCompleted?.(data?.phase);
        break;
      case 'cab_section_ready':
        cbs.onSectionReady?.(data?.section);
        break;
      case 'cab_skeleton_ready':
        cbs.onSkeletonReady?.();
        break;
      case 'cab_completed':
        cbs.onCompleted?.(data);
        break;
      case 'cab_error':
        cbs.onError?.(data);
        break;
      case 'cab_paused':
        cbs.onPaused?.(data);
        break;
      case 'cab_cancelled':
        cbs.onCancelled?.(data);
        break;
      default:
        console.warn(`📡 CAB Hook: Unknown event type [${type}]`);
    }
  }, []);

  const connect = useCallback(() => {
    if (!projectId || !user) return;

    const token = localStorage.getItem('token') || '';
    if (!token) {
      console.warn('📡 CAB Hook: No auth token found in localStorage');
      return;
    }

    const ws = cabWebSocketManager.getOrCreate(projectId, token, {
      onMessage: handleMessage,
      onConnect: () => setIsConnected(true),
      onDisconnect: () => setIsConnected(false),
      onReconnect: () => setIsConnected(true),
    });
    wsRef.current = ws;

    if (!ws.isConnected()) {
      ws.connect();
    }
  }, [projectId, user, handleMessage]);

  const disconnect = useCallback(() => {
    if (projectId) {
      cabWebSocketManager.disconnect(projectId);
      wsRef.current = null;
      setIsConnected(false);
    }
  }, [projectId]);

  // Auto-connect when enabled and projectId is available
  useEffect(() => {
    console.log(`📡 CAB Hook: enabled=${enabled}, projectId=${projectId}, user=${!!user}`);
    if (enabled && projectId && user) {
      console.log('📡 CAB Hook: Auto-connecting...');
      connect();
    } else if (!enabled) {
      console.log('📡 CAB Hook: Disabled, disconnecting...');
    }

    return () => {
      // Disconnect on unmount
      if (projectId) {
        console.log('📡 CAB Hook: Cleanup — disconnecting');
        cabWebSocketManager.disconnect(projectId);
      }
    };
  }, [enabled, projectId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isConnected, connect, disconnect };
}
